import OpenAI from "openai";
import { getOpenAIBaseUrl, getOpenAIKey } from "@/lib/client/openai-key";

/**
 * OpenAI SDK client, browser-first (client-only pivot).
 *
 * In the browser the user's own key + base URL (lib/client/openai-key)
 * drive every call — `dangerouslyAllowBrowser` is correct usage under
 * BYOK, where the key belongs to the person operating the browser.
 * Outside the browser (unit tests, mock-server dev tooling) the
 * OPENAI_API_KEY / OPENAI_BASE_URL env fallbacks remain.
 */

interface ClientConfig {
  apiKey: string;
  baseURL: string | undefined;
}

function resolveConfig(): ClientConfig {
  if (typeof window !== "undefined") {
    const browserKey = getOpenAIKey();
    if (browserKey) {
      return { apiKey: browserKey, baseURL: getOpenAIBaseUrl() };
    }
  }

  const apiKey = process.env.OPENAI_API_KEY;
  const baseURL = process.env.OPENAI_BASE_URL;
  const isDevelopment = process.env.NODE_ENV === "development";

  // In development with mock server, use placeholder if no real key
  if (isDevelopment && baseURL && !apiKey) {
    return { apiKey: "mock-api-key", baseURL };
  }

  // Placeholder keeps module import safe during build; calls made without
  // a real key fail at request time with a clear 401.
  return { apiKey: apiKey || "placeholder-no-key-set", baseURL };
}

/**
 * The SDK feature-detects FormData support by fetching "data:," — unless
 * the fetch it's given exposes the Response class directly. Under our CSP
 * (connect-src has no data:) that probe logs violations on the first
 * upload, so hand the SDK a fetch that carries Response and no probe runs.
 * The static e2e (CSP smoke) regression-tests this.
 */
function cspSafeFetch(): typeof globalThis.fetch | undefined {
  if (typeof globalThis.fetch !== "function") return undefined;
  return Object.assign(
    (...args: Parameters<typeof globalThis.fetch>) => globalThis.fetch(...args),
    { Response: globalThis.Response },
  );
}

// Lazy singleton, rebuilt whenever the resolved key/baseURL changes
// (e.g. the user replaces the key in settings).
let _openai: OpenAI | null = null;
let _configKey: string | null = null;

export function getOpenAIClient(): OpenAI {
  const config = resolveConfig();
  const configKey = `${config.apiKey}\0${config.baseURL ?? ""}`;
  if (!_openai || _configKey !== configKey) {
    _openai = new OpenAI({
      apiKey: config.apiKey,
      baseURL: config.baseURL,
      fetch: cspSafeFetch(),
      // SDK retries 408/409/429/5xx twice with exponential backoff.
      maxRetries: 2,
      dangerouslyAllowBrowser: typeof window !== "undefined",
    });
    _configKey = configKey;
  }
  return _openai;
}

/**
 * Reset the client singleton so the next getter call rebuilds it —
 * tests use this for isolation.
 * @internal Test-only export.
 */
export function _resetClient(): void {
  _openai = null;
  _configKey = null;
}
